<template>
<v-container fluid>
	<div id="map"></div>
</v-container>
</template>

<style lang="stylus">
.container
	height 100%
#map
	height 100%
	width 100%
	z-index 0
</style>

<script>
import Vuex from 'vuex'
import L from 'leaflet';
import '../../node_modules/leaflet/dist/leaflet.css';
import api from '../assets/js/api-instance'
import defaultTileLayers from '../assets/map-layers.json'
import * as moment from 'moment'

function metres2pixels (zoomLevel, latitude, earthRadius = 6378137) {
	const C = 2*Math.PI*earthRadius; // Earth circumference
	const φ = latitude / 180 * Math.PI; // Latitude in radians
	return (C * Math.cos(φ)/Math.pow(2, zoomLevel+8)); // Distance in pixels
}

export default {
	name: "FollowMap",
	
	data () {
		return {
			map: null,
			layers: {},
			ws: {},
			selfPosition: null
		};
	},
	
	watch: {
		async visibleLayers () {
			// Remove layers
			this.map.eachLayer(l => l instanceof L.FeatureGroup && l.remove());

			// Add layers
			for (const token_id of this.visibleLayers) {
				this.plotLayer(token_id);
			}
			this.plotLatest();
		},
		
		fixes () {
			for (const token_id in this.fixes) {
				if (this.visibleLayers.includes(token_id)) {
					this.plotLayer(token_id);
				}
			}
			this.plotLatest();
		},
		
		filtered () {
			// FIXME refactor this.layers. It should
			// have layers grouped according to their type,
			// e.g.:
			// this.layers
			//   - tokens
			//     - token_1 (featureGroup)
			//     - token_2 (featureGroup)
			//   - filtered
			//     - filtered_token_1 (featureGroup)
			//     - filtered_token_2 (featureGroup)
			//   - selected
			//     - selected_token_1 (featureGroup)
			//     - selected_token_2 (featureGroup)
			//   - lkp
			//     - lkp_token_1 (marker)
			//     - lkp_token_2 (marker)
			//   - own
			//     (marker)
			for (const layer in this.layers) {
				if (/^f-/.test(layer)) {
					this.layers[layer].remove();
				}
			}
			
			for (const token_id in this.filtered) {
				if (this.visibleLayers.includes(token_id)) {
					let layer = [];
					for (const fix of this.filtered[token_id]) {
						const color = fix.color || "white";
						const radius = fix.radius || 3;
						const style = fix.style || {color, radius};
						let marker = L.circleMarker([fix.latitude, fix.longitude], style);
						layer.push(marker);
					}
					const featureGroup = L.featureGroup(layer);
					featureGroup.addTo(this.map);
					
					if (this.layers["f-"+token_id]) {
						this.layers["f-"+token_id].remove();
					}
					this.layers["f-"+token_id] = featureGroup;
				}
			}
		},
		
		"user.tokens": {
			handler () {
				// Remove non-public layers on logout
				if (!this.user.user_id) {
					// TODO Public layers not implemented yet. Remove all.
					this.map.eachLayer(l => l instanceof L.FeatureGroup && l.remove());
				}
			}
		}
	},
	
	computed: {
		...Vuex.mapState(["user", "tokenData"]),
		visibleLayers () {
			return Object.keys(this.tokenData.tokenState)
			.filter(token_id => this.tokenData.tokenState[token_id].visible);
		},
		fixes () {
			return this.$store.state.tokenData.fixes;
		},
		filtered () {
			return this.$store.state.tokenData.filtered;
		},
		latestKnownPositions () {
			let markers = [];
			for (const token_id of this.visibleLayers) {
				if (this.fixes[token_id] && this.fixes[token_id].length) {
					markers.push(this.fixes[token_id][0]);
				}
			}
			return markers;
		}
	},
	
	methods: {
		...Vuex.mapActions(["setTokenVisible"]),
		
		messageHandler (message) {
			if (message.type == "subscription") {
				this.tracking(message.payload.add);
			}
		},
		
		tracking (fixes) {
			if (Array.isArray(fixes)) {
				for (const fix of fixes) {
					const token_id = fix.token_id;
					const tokenState = this.tokenData.tokenState[token_id] || {};
					if (tokenState.visible && tokenState.tracking) {
						this.map.flyTo([fix.latitude, fix.longitude]);
					}
					if (this.layers[fix.token_id]) {
						const style = Object.assign({ color: "orange", radius: 2}, fix.style);
						this.plotFix(fix, {style}).addTo(this.layers[fix.token_id]);
					}
				}
			}
			this.plotLatest();
		},
		
		plotFix (fix, opts = {}) {
			const title = `
${moment(fix.tstamp || fix.timestamp).format("YYYY-MM-DD HH:mm:ss")}<br/>
<table>
<tr><td style="text-align:right">${Number(fix.speed * 3.6).toFixed(0)}</td><td>km/h</td></tr>
<tr><td style="text-align:right">${Number(fix.altitude).toFixed(1)}</td><td>m AMSL</td></tr>
</table>`;
			const style = Object.assign({
				radius: 2,
				title
			}, fix.metadata.style, fix.style, opts.style);
			let marker = L.circleMarker([fix.latitude, fix.longitude], style);
			marker.bindTooltip(title);
			return marker;
		},
		
		plotLayer (token_id) {
			const token = this.tokenData.tokens.find(t => t.token_id == token_id);
			const fixes = this.fixes[token_id] || [];
			let layer = [];
			const tokenStyle = token && token.metadata.style || {};
			let opacity = 1;
			const opacityStep = 0.9/fixes.length;
			
			for (const fix of fixes) {
				const style = Object.assign({opacity}, tokenStyle);
				layer.push(this.plotFix(fix, {style}));
				opacity -= opacityStep;
			}
			
			const featureGroup = L.featureGroup(layer);
			featureGroup.addTo(this.map);
			this.layers[token_id] && this.layers[token_id].remove();
			this.layers[token_id] = featureGroup;
		},
		
		plotLatest () {
			const latestKnownPositionMarkers = this.latestKnownPositions
			.filter( fix => this.visibleLayers.includes(fix.token_id) )
			.map( fix => {
				const zoomScale = metres2pixels(this.map.getZoom(), fix.latitude);
				const radius = Math.max(api.fixAccuracy2D(fix) / zoomScale, 2) || 2;
				//console.log("LKP", api.fixAccuracy2D(fix), this.map.getZoom(), zoomScale, radius);
				const style = {
					color: "red",
					opacity: 0.05,
					radius
				};
				return this.plotFix(fix, {style});
			});
			
			if (this.layers.lkp) {
				this.layers.lkp.clearLayers();
			}
			
			if (latestKnownPositionMarkers.length) {
				latestKnownPositionMarkers.forEach(marker => marker.addTo(this.layers.lkp));
			}
		},
		
		plotSelf () {
			if (this.selfPosition) {
				const fix = this.selfPosition;
				const zoomScale = metres2pixels(this.map.getZoom(), fix.latitude);
				const radius = Math.max(api.fixAccuracy2D(fix) / zoomScale, 2) || 2;
				//console.log("LKP", api.fixAccuracy2D(fix), this.map.getZoom(), zoomScale, radius);
				const style = {
					color: "yellow",
					opacity: radius <= 2 ? .85 : 0.15,
					radius
				};
				this.layers.self.clearLayers();
				this.plotFix(fix, {style}).addTo(this.layers.self);
			}
		}
	},
	
	async mounted () {
		
		this.map = L.map('map')
		this.map.setView([7, 40], 3);
		this.map.invalidateSize();
		
		let tileLayers = defaultTileLayers;
		
		try {
			// The server administrator may have defined additional layers
			// available in his installation. We try to fetch them.
			const res = await api.fetch("/static/map-layers.json");
			if (res.ok) {
				if (!res.headers.get("Content-Type").toLowerCase().includes("application/json")) {;
					console.warn("map-layers.json found, but has an unexpected content-type");
					console.warn("Content-Type: ", res.headers.get("Content-Type"));
				}
				const body = await res.json();
				if (Array.isArray(body) && body.length) {
					tileLayers = body;
				}
			}
		} catch (err) {
			console.warn(err);
		}
		
		this.layers.lkp = L.layerGroup();
		this.layers.lkp.addTo(this.map);
		
		let overlayLayers = {
			"Last known positions": this.layers.lkp
		};
		
		if ("geolocation" in navigator) {
			this.layers.self = L.layerGroup();
			overlayLayers["Own position"] = this.layers.self;
		}
		
		let baseLayers = {};
		tileLayers.forEach(layer => {
			let tile = L.tileLayer(layer.url, layer.options);
			baseLayers[layer.title] = tile;
		});
		
		if (tileLayers.length) {
			baseLayers[tileLayers[tileLayers.length-1].title].addTo(this.map);
			this.mapControl = L.control.layers(baseLayers, overlayLayers).addTo(this.map);
		}
		
		this.$root.$on('zoom', (payload) => {
			const layer = this.layers[payload.token_id];
			if (layer) {
				this.map.fitBounds(layer.getBounds().pad(0.1));
			}
		});
		
		this.$root.$on('message', (message) => {
			console.log("GOT MESSAGE", message);
			this.messageHandler(message);
		});
		
		if (this.$route.params.token_ids) {
			const layers = this.$route.params.token_ids.split(/\s*;\s*/);
			//console.log("Make visible:", layers);
			
			// FIXME This is a kludge.
			// The whole routing logic needs to be rethought.
			// Better to control it from one place, or individually
			// within each component?
			setTimeout( async () => {
				let mapBounds = L.latLngBounds();
				for (const token_id of layers) {
					await this.setTokenVisible({token_id, visible: true});
					mapBounds.extend(this.layers[token_id].getBounds());
					//console.log("Processed", token_id);
				}
				
				this.map.fitBounds(mapBounds.pad(0.1));
			}, 100);
		}
		
		const handleMapEvents = (ev) => {
			if (!ev) {
				return;
			} else if (ev.type == "overlayadd") {
				if (ev.layer === this.layers.self) {
					console.log("START TRACKING");
					this.map.locate({
						watch: true,
						enableHighAccuracy: true
					});
				}
			} else if (ev.type == "overlayremove") {
				if (ev.layer === this.layers.self) {
					console.log("STOP TRACKING");
					this.map.stopLocate();
					this.selfPosition = null;
					this.layers.self.clearLayers();
				}
			}
		};
		
		const handleLocationEvents = (ev) => {
			if (!ev) {
				return;
			} else if (ev.type == "locationerror") {
				console.log("LOCATION ERROR", ev.message, ev.code);
			} else if (ev.type == "locationfound") {
				this.selfPosition = ev;
				this.plotSelf();
			}
		};
		
		
		this.map.on('zoomend', (ev) => {
			this.plotLatest();
			this.plotSelf();
		});
		
		this.map.on('overlayadd', handleMapEvents);
		this.map.on('overlayremove', handleMapEvents);
		this.map.on('locationfound', handleLocationEvents);
		this.map.on('locationerror', handleLocationEvents);
	}
}
</script>
